/* eslint-disable @next/next/no-img-element */
import { FC, useCallback, useState } from 'react'
import dynamic from 'next/dynamic'
import { Row, Col, Text, Collapse, CollapseItem, Icon, Image, Link } from '@/client/components'
import { useRouterPush, useWindowSize } from '@/client/hooks'
import lottie from './lottie.json'
import * as S from './styles'
import { FinishingSheet } from '../FinishingSheet'
import { PantoneColors } from '../PantoneColors'
import { TileBlock } from '../TileBlock'
import { ImportantThingsProps } from '../types'

// Load Lottie-Component only in case of client side rendering (exclude SSR)
const Lottie = dynamic(() => import('lottie-react'), { ssr: false })

export const ImportantThings: FC<ImportantThingsProps> = ({
  important_things,
  more_infos,
  image_list,
  warning_cards,
  guide,
  guide_full_width,
  finishing_techniques_title,
  finishing,
  pentonetitleleft,
  pentonedescleft,
  pentonetitleright,
  pentonedescright
}) => {
  const { breakpoint } = useWindowSize()
  const [searchValue, setSearchValue] = useState<string>('')
  const [search_input_label] = useState('')
  const { defaultLocale } = useRouterPush()
  const handleChangeSearchValue = useCallback(({ currentTarget: { value } }) => {
    setSearchValue(value)
  }, [])
  return (
    <>
      <S.SummaryInfoWrapper>
        <S.SummaryInfoBlock>
          <Row className='summary-link-row'>
            <Col xs='12' xl='12'>
              <Text
                as='h1'
                size='lg'
                className='banner-header-primary'
                family='text'
                weight='semiBlack'
                margin='0 0 1.5rem'
                itemProp='name'
              >
                {important_things.title}
              </Text>
              <Row isFull>
                {important_things.details.map(({ key, value, ico }) => (
                  <Col xs='6' md='4' lg='2' className='SummeryCol' key={key}>
                    <S.SummeryIcon key={key}>
                      {(() => {
                        switch (ico) {
                          case 'pdf':
                            return <img src='/img/print-data-delivery/pdf.png' alt='PDF' />
                          case 'bleed':
                            return <img src='/img/print-data-delivery/bleed.png' alt='Outside Bleed' />
                          case 'safety':
                            return <img src='/img/print-data-delivery/inside.png' alt='Safety Margin inside' />
                          case 'color-mode':
                            return <img src='/img/print-data-delivery/cmyk.png' alt='Colors Mode' />
                          case 'font-path':
                            return <img src='/img/print-data-delivery/fonts.png' alt='Fonts' />
                          case 'small-cap':
                            return <img src='/img/print-data-delivery/x.png' alt='Fonts' />
                          case 'high-contrast':
                            return <img src='/img/print-data-delivery/vector.png' alt='High Contrast' />

                          case 'min-resolution':
                            return <img src='/img/print-data-delivery/min-resolution.png' alt='min resolution' />
                          case 'standared-resolution':
                            return (
                              <img src='/img/print-data-delivery/standared-resolution.png' alt='standared resolution' />
                            )
                          case 'standared-resolution-line-art':
                            return (
                              <img
                                src='/img/print-data-delivery/standared-resolution-line-art.png'
                                alt='standared resolution line art'
                              />
                            )
                          case 'embedded-pictures':
                            return <img src='/img/print-data-delivery/pic.png' alt='Embedded Picture' />
                          case 'print-proof':
                            return <img src='/img/print-data-delivery/print-proof.png' alt='Print Proof' />
                          case 'data-centered':
                            return <img src='/img/print-data-delivery/data-centered.png' alt='data centered' />

                          default:
                            return <img src='/img/print-data-delivery/data-centered.png' alt='data centered' />
                        }
                      })()}
                      <Text
                        as='p'
                        size='xs'
                        className='banner-header-primary'
                        family='text'
                        weight='normal'
                        margin='1rem 0 0'
                        itemProp='name'
                      >
                        {key}
                      </Text>
                      <Text
                        as='p'
                        size='xs'
                        className='banner-header-primary'
                        family='text'
                        weight='normal'
                        margin='0 0'
                        itemProp='name'
                      >
                        {value}
                      </Text>
                    </S.SummeryIcon>
                  </Col>
                ))}
              </Row>
            </Col>
            <Col xs='12' xl='5'>
              {defaultLocale === 'au' && (
                <S.InlineLabel>
                  <nav>
                    {important_things.pdf_list.map(({ link, title, subtitle }) => (
                      <Link key={`key_${link}`} href={link} itemProp='name' title={title}>
                        <S.InlineLabelEach key={title}>
                          <S.InlineImage>
                            <Icon id='pdf-cco' name='pdf-cco' size='s6' color='#00273E' />
                          </S.InlineImage>
                          <S.InlineText>
                            {subtitle}
                            <Text
                              as='p'
                              size='xs'
                              className=''
                              margin='0'
                              family='text'
                              weight='normal'
                              itemProp='name'
                            >
                              {title}
                            </Text>
                          </S.InlineText>
                        </S.InlineLabelEach>
                      </Link>
                    ))}
                  </nav>
                </S.InlineLabel>
              )}
            </Col>
          </Row>
          <Row>
            <Col xs='12' lg='6'>
              <S.ImageAligner>
                <Lottie animationData={lottie} loop={true} />
              </S.ImageAligner>
            </Col>
            <Col xs='12' lg='6'>
              <S.StructureLabel>
                <Text as='h2' size='xxlg' className='' family='text' weight='semiBlack' margin='0 0 1' itemProp='name'>
                  {important_things.label_structure_name}
                </Text>
                <Text
                  as='p'
                  size='baseroot'
                  className='structure-paragraph'
                  family='text'
                  weight='normal'
                  margin='0 0 2rem 0'
                  itemProp='name'
                >
                  {important_things.label_structure_details}
                </Text>
              </S.StructureLabel>
              <div>
                <S.NumberSetWrapper>
                  {important_things.label_structure_list.map(
                    ({ label_structure_list_key, label_structure_list_type, label_structure_list_name }) => (
                      <S.NumberSetEach key={label_structure_list_key} type={label_structure_list_type}>
                        <S.CountBlock className='icon'>{label_structure_list_key}</S.CountBlock>
                        <div>
                          <Text
                            as='p'
                            size='xs'
                            className='counter-block-p'
                            family='text'
                            weight='normal'
                            margin='0 0 0'
                            itemProp='name'
                          >
                            {label_structure_list_name}
                          </Text>
                        </div>
                      </S.NumberSetEach>
                    )
                  )}
                </S.NumberSetWrapper>
              </div>
            </Col>
          </Row>
        </S.SummaryInfoBlock>
      </S.SummaryInfoWrapper>
      {guide_full_width && <TileBlock guide={guide_full_width} width='full'></TileBlock>}
      {guide && <TileBlock guide={guide} width='half'></TileBlock>}
      {finishing_techniques_title && (
        <>
          <FinishingSheet finishing_techniques_title={finishing_techniques_title} finishing={finishing} />
          <PantoneColors
            pentonetitleleft={pentonetitleleft}
            pentonedescleft={pentonedescleft}
            pentonetitleright={pentonetitleright}
            pentonedescright={pentonedescright}
          />
        </>
      )}

      <S.CollapseRow isFull={breakpoint !== 'desktop'}>
        <Col xs={12} lg={7} xl={8}>
          <S.Search
            isBlock
            isPill
            id='input-search'
            type='search'
            placeholder={search_input_label}
            value={searchValue}
            onChange={handleChangeSearchValue}
            autoComplete='off'
            rightElement={
              <S.SearchIcon aria-label='Ir página de busca'>
                <Icon name='search' size='s5' />
              </S.SearchIcon>
            }
          />
          <Collapse style={{ marginBottom: '3rem' }}>
            {more_infos
              .filter(
                (item) =>
                  item.title.toLowerCase().includes(searchValue) || item.content.toLowerCase().includes(searchValue)
              )
              .map(({ title, content }) => (
                <CollapseItem key={title} title={title}>
                  <S.CollapseContent dangerouslySetInnerHTML={{ __html: content }} />
                </CollapseItem>
              ))}
          </Collapse>
        </Col>

        <Col xs={12} lg={5} xl={4} hd={3} hdOffset={1}>
          {image_list.map(({ title, image }) => (
            <S.ImageWrapper key={title}>
              <Image alt={image?.alt || title} src={image.url} width={image.width} height={image.height} />
            </S.ImageWrapper>
          ))}

          {warning_cards.map(({ title, warning }) => (
            <S.WarningBox key={title} type='gray'>
              <Icon name='info-bulb' size={breakpoint === 'desktop' ? 's6' : 's5'} color='#00263E' />
              <Text dangerouslySetInnerHTML={{ __html: warning }} margin='1rem 0 0' />
            </S.WarningBox>
          ))}
        </Col>
      </S.CollapseRow>
    </>
  )
}
